import { type AtticErrorFragment, type AtticIntakeInput, AtticLotSource } from "@dashboard/graphql";

import { normalizeSku, parseCost, parseInteger } from "./utils";

export interface AtticIntakeLine {
  /** Local only: React's key for the row. */
  key: number;
  sku: string;
  location: string;
  quantity: string;
  unitCost: string;
}

export interface AtticIntakeFormData {
  source: AtticLotSource;
  reference: string;
  supplier: string;
  currency: string;
  notes: string;
  lines: AtticIntakeLine[];
}

let nextKey = 1;

export const emptyIntakeLine = (location = ""): AtticIntakeLine => ({
  key: nextKey++,
  sku: "",
  location,
  quantity: "1",
  unitCost: "",
});

export const emptyIntakeForm = (): AtticIntakeFormData => ({
  source: AtticLotSource.DISTRIBUTOR,
  reference: "",
  supplier: "",
  currency: "USD",
  notes: "",
  lines: [emptyIntakeLine()],
});

/** A line the user has started: anything typed but the defaults. */
const isTouched = (line: AtticIntakeLine) =>
  line.sku.trim() !== "" || line.location.trim() !== "" || line.unitCost.trim() !== "";

export interface LineProblems {
  sku?: boolean;
  location?: boolean;
  quantity?: boolean;
  unitCost?: boolean;
}

/** What is wrong with a line before it is sent; empty when nothing. */
export const lineProblems = (line: AtticIntakeLine): LineProblems => {
  const quantity = parseInteger(line.quantity);
  const problems: LineProblems = {};

  if (normalizeSku(line.sku) === "") {
    problems.sku = true;
  }

  if (quantity === null || quantity <= 0) {
    problems.quantity = true;
  }

  if (parseCost(line.unitCost) === null) {
    problems.unitCost = true;
  }

  return problems;
};

/** The lines that will be sent: the touched ones. Blank trailing rows are ignored. */
export const linesToSend = (lines: AtticIntakeLine[]) => lines.filter(isTouched);

/** Whether the form can be sent: at least one line, every sent line complete. */
export const isIntakeValid = (data: AtticIntakeFormData): boolean => {
  const lines = linesToSend(data.lines);

  return (
    lines.length > 0 &&
    lines.every(line => Object.keys(lineProblems(line)).length === 0) &&
    /^[A-Za-z]{3}$/.test(data.currency.trim())
  );
};

/** Copies and total cost of the complete lines, for the running total. */
export const intakeTotals = (lines: AtticIntakeLine[]) =>
  linesToSend(lines).reduce(
    (totals, line) => {
      const quantity = parseInteger(line.quantity);
      const cost = parseCost(line.unitCost);

      if (quantity === null || quantity <= 0 || cost === null) {
        return totals;
      }

      return { units: totals.units + quantity, cost: totals.cost + quantity * cost };
    },
    { units: 0, cost: 0 },
  );

/** The mutation's input. Call only when `isIntakeValid`. */
export const formToIntakeInput = (data: AtticIntakeFormData): AtticIntakeInput => ({
  source: data.source,
  reference: data.reference.trim(),
  supplier: data.supplier.trim(),
  currency: data.currency.trim().toUpperCase(),
  notes: data.notes,
  lines: linesToSend(data.lines).map(line => ({
    sku: normalizeSku(line.sku),
    // Blank: the API's UNPLACED holding location, to be placed later.
    location: line.location.trim() === "" ? null : line.location.trim().toUpperCase(),
    quantity: parseInteger(line.quantity) ?? 0,
    unitCost: parseCost(line.unitCost) ?? 0,
  })),
});

const LINE_FIELD = /^lines\[(\d+)\]\.(\w+)$/;

/**
 * Where a refusal belongs. The API names a line's field `lines[2].sku`,
 * counting the lines it was sent; that index is mapped back to the form's
 * line (blank rows were not sent). A refusal with no line goes on the form.
 */
export const placeIntakeErrors = (
  errors: AtticErrorFragment[],
  lines: AtticIntakeLine[],
): { byLine: Map<number, AtticErrorFragment[]>; form: AtticErrorFragment[] } => {
  const sent = linesToSend(lines);
  const byLine = new Map<number, AtticErrorFragment[]>();
  const form: AtticErrorFragment[] = [];

  errors.forEach(error => {
    const match = error.field ? LINE_FIELD.exec(error.field) : null;
    const line = match ? sent[Number(match[1])] : undefined;

    if (line) {
      byLine.set(line.key, [...(byLine.get(line.key) ?? []), error]);
    } else {
      form.push(error);
    }
  });

  return { byLine, form };
};

/** The field a line refusal is about (`sku`, `location`, ...), or null. */
export const lineErrorField = (error: AtticErrorFragment): string | null =>
  error.field ? (LINE_FIELD.exec(error.field)?.[2] ?? null) : null;
